<template>
  <RevCard class="flex-1 p-24">
    <RevTag
      v-if="isMissingLegalDetails"
      class="mb-8"
      :label="i18n(translations.toDoLabel)"
      variant="warning"
    />
    <h2 class="heading-2 mb-8">
      {{ i18n(translations.title) }}
    </h2>
    <WithSkeletonLoader :is-loading="pending">
      <template v-if="legalDetailsMetadata">
        <div
          v-for="legalDetail in formattedLegalDetails"
          :key="legalDetail.label"
          class="mb-16"
        >
          <div class="caption mb-4">
            {{ legalDetail.label }}
          </div>
          <div class="body-1 flex items-center justify-between">
            <span v-if="legalDetail.value">
              {{ legalDetail.value }}
            </span>
            <div v-if="!legalDetail.value" class="flex items-center">
              <IconWarning class="fill-danger mr-12" />
              <RevLink @click="handleOpenModal">
                {{ i18n(translations.emptyLegalDetail) }}
              </RevLink>
            </div>
          </div>
        </div>
        <RevButton
          full-width="never"
          :icon="IconEdit"
          variant="secondary"
          @click="handleOpenModal"
        >
          {{ i18n(translations.editButton) }}
        </RevButton>
        <OverviewModal
          :form-type="legalDetailsMetadata.formType"
          :is-missing-legal-details
          :legal-details
          :legal-details-metadata
          :organization-types="legalDetailsMetadata.organizationTypes"
          @update-form="handleFormUpdated"
        />
      </template>
    </WithSkeletonLoader>
  </RevCard>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed } from 'vue'

import { sellerProfileAPI } from '@backmarket/http-api'
import type { GetLegalDetailsMetadata } from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-profile'
import { useHttpLazyFetch } from '@backmarket/nuxt-module-http/useHttpLazyFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'
import { RevTag } from '@ds/components/Tag'
import { IconEdit } from '@ds/icons/IconEdit'
import { IconWarning } from '@ds/icons/IconWarning'

import { MODAL_NAMES } from '../../../../Profile.constants'
import WithSkeletonLoader from '../../../../components/WithSkeletonLoader/WithSkeletonLoader.vue'

import { PARAM_KEYS } from './Overview.constants'
import translations from './Overview.translations'
import OverviewModal from './components/OverviewModal/OverviewModal.vue'

const MODAL_NAME = MODAL_NAMES.EDIT_LEGAL_DETAILS

const props = defineProps<{
  legalDetailsMetadata: GetLegalDetailsMetadata | null
  isMissingLegalDetails: boolean
}>()

const i18n = useI18n()
const { openErrorToast } = useTheToast()

const {
  data: legalDetails,
  error,
  pending,
  refresh,
} = useHttpLazyFetch(sellerProfileAPI.getProfileViewLegalDetails, {
  queryParams: { views: 'legalDetails' },
})

if (error.value) {
  openErrorToast({
    content: getApiErrorMessage(error.value as unknown as Error),
  })
}

const formattedLegalDetails = computed(() =>
  props.legalDetailsMetadata?.legalDetailTypes.map(({ label, code }) => {
    const value =
      legalDetails.value?.[PARAM_KEYS[code] as keyof typeof legalDetails.value]

    return {
      label,
      value: typeof value === 'string' ? value : value?.label,
    }
  }),
)

function handleOpenModal() {
  openModal(MODAL_NAME)
}

function handleFormUpdated() {
  refresh()
}
</script>
